import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Download, Loader2, ShieldCheck, Settings, ListChecks, Users,
  FileText, CheckCircle2, AlertTriangle, Plus, Pencil, Trash2, User,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const STATO_BADGE = {
  completato: "bg-emerald-500/15 text-emerald-400",
  in_corso: "bg-amber-500/15 text-amber-400",
  iscritto: "bg-blue-500/15 text-blue-400",
  bloccato: "bg-white/5 text-slate-400",
  da_iniziare: "bg-blue-500/15 text-blue-300",
};

export default function FormazioneAdminSection() {
  const [view, setView] = useState("corsisti"); // corsisti | moduli | richieste | audit | settings
  const [openUser, setOpenUser] = useState(null);

  return (
    <div className="space-y-6" data-testid="formazione-admin-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-extrabold">Formazione → Corsisti</h2>
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "corsisti", l: "Corsisti", icon: Users },
            { id: "moduli", l: "Moduli", icon: ListChecks },
            { id: "richieste", l: "Richieste verifica", icon: AlertTriangle },
            { id: "audit", l: "Audit Log", icon: ShieldCheck },
            { id: "settings", l: "Impostazioni", icon: Settings },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`formazione-admin-tab-${t.id}`}
              onClick={() => { setView(t.id); setOpenUser(null); }}
              className={`px-3 py-2 rounded-lg text-[12.5px] font-bold inline-flex items-center gap-1.5 ${view === t.id ? "bg-[#f59e0b] text-[#0a1020]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.l}
            </button>
          ))}
        </div>
      </div>
      {view === "corsisti" && (openUser ? <ReportCorsista userId={openUser} onBack={() => setOpenUser(null)} /> : <CorsistiTable onOpen={setOpenUser} />)}
      {view === "moduli" && <ModuliCrud />}
      {view === "richieste" && <RichiesteVerifica />}
      {view === "audit" && <AuditLog />}
      {view === "settings" && <SettingsPanel />}
    </div>
  );
}

function CorsistiTable({ onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/admin/formazione/corsisti`, { withCredentials: true })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 overflow-x-auto">
      <table className="w-full text-[13px]" data-testid="corsisti-table">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-400 border-b border-white/10">
            {["Corsista", "Tessera", "Corso", "Avanzamento", "Presenza media", "Ultimo accesso", "Stato", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => onOpen(r.user_id)} data-testid={`corsista-row-${r.user_id}`}>
              <td className="px-4 py-3 font-bold text-white">{r.nome}</td>
              <td className="px-4 py-3 text-slate-300">{r.numero_tessera || "—"}</td>
              <td className="px-4 py-3 text-slate-300">{r.corso}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-20 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#f59e0b]" style={{ width: `${r.avanzamento}%` }} /></div>
                  <span className="font-bold text-white">{r.avanzamento}%</span>
                </div>
              </td>
              <td className="px-4 py-3 text-slate-300">{r.presenza_media}%</td>
              <td className="px-4 py-3 text-slate-400">{fmtDT(r.ultimo_accesso)}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase ${STATO_BADGE[r.stato] || STATO_BADGE.iscritto}`}>{r.stato.replace("_", " ")}</span></td>
              <td className="px-4 py-3 text-[#f59e0b] font-bold">Apri →</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Nessun corsista iscritto al percorso formativo.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReportCorsista({ userId, onBack }) {
  const [rep, setRep] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/formazione/corsisti/${userId}`, { withCredentials: true })
      .then((r) => setRep(r.data)).catch(() => setRep(false));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const exportFile = async (fmt) => {
    const r = await axios.get(`${API}/api/admin/formazione/corsisti/${userId}/report.${fmt}`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `report_formazione.${fmt}`; a.click();
    URL.revokeObjectURL(url);
  };

  const setIdentita = async (stato) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/formazione/identita/${userId}`, { stato }, { withCredentials: true });
      load();
    } finally { setSaving(false); }
  };

  const validaModulo = async (moduloId) => {
    const motivo = window.prompt("Motivo della validazione manuale (registrato nell'Audit Log):");
    if (!motivo) return;
    await axios.post(`${API}/api/admin/formazione/override`, { user_id: userId, modulo_id: moduloId, azione: "valida_modulo", motivo }, { withCredentials: true });
    load();
  };

  if (rep === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  if (rep === false) return <div className="text-red-400">Corsista non trovato.</div>;
  const c = rep.corsista;

  return (
    <div className="space-y-5" data-testid="report-corsista">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-[13px] font-bold text-[#f59e0b] hover:underline"><ArrowLeft className="h-4 w-4" /> Torna ai corsisti</button>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"><Download className="h-4 w-4 mr-1.5" /> 📄 Esporta report PDF</Button>
          <Button size="sm" onClick={() => exportFile("csv")} data-testid="export-csv-btn" variant="outline" className="border-white/20 text-white font-bold hover:bg-white/10"><Download className="h-4 w-4 mr-1.5" /> 📊 CSV</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
        <h3 className="font-extrabold text-lg mb-4">Report formativo del corsista</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-[13px]">
          {[
            ["Nome", `${c.nome || ""} ${c.cognome || ""}`], ["Numero tessera", c.numero_tessera || "—"],
            ["Data iscrizione", fmtDT(rep.data_iscrizione)], ["Primo accesso", fmtDT(rep.primo_accesso)],
            ["Ultimo accesso", fmtDT(rep.ultimo_accesso)], ["Moduli completati", `${rep.moduli_completati} / ${rep.moduli_totali}`],
            ["Ore completate", `${rep.ore_completate} h`], ["Percentuale corso", `${rep.percentuale_corso}%`],
            ["Presenza media", `${rep.presenza_media}%`],
          ].map(([l, v]) => (
            <div key={l}><div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold">{l}</div><div className="font-bold text-white">{v}</div></div>
          ))}
        </div>
      </div>

      {/* VERIFICA IDENTITÀ */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="verifica-identita-panel">
        <h3 className="font-extrabold mb-3 flex items-center gap-2"><User className="h-4 w-4 text-[#f59e0b]" /> Verifica identità (controllo umano)</h3>
        <div className="flex flex-wrap items-center gap-5">
          <div className="text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Foto tesseramento</div>
            {c.foto_tessera_id ? (
              <img src={`${API}/api/files/${c.foto_tessera_id}`} alt="Foto tessera" className="h-28 w-28 rounded-xl object-cover border border-white/20" />
            ) : (
              <div className="h-28 w-28 rounded-xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-[11px] text-slate-500 px-2">Nessuna foto caricata</div>
            )}
          </div>
          <div className="flex-1 min-w-[220px] space-y-2">
            <div className="text-[13px]">
              Stato attuale: <span className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold uppercase ${rep.identita.stato === "verificata" ? "bg-emerald-500/15 text-emerald-400" : rep.identita.stato === "non_verificata" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="identita-stato">{(rep.identita.stato || "da_verificare").replace("_", " ")}</span>
            </div>
            {rep.identita.verified_by && <div className="text-[12px] text-slate-400">Verificata da: {rep.identita.verified_by} · {fmtDT(rep.identita.verified_at)}</div>}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" disabled={saving} onClick={() => setIdentita("verificata")} data-testid="identita-si-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">Identità verificata: SÌ</Button>
              <Button size="sm" disabled={saving} onClick={() => setIdentita("non_verificata")} data-testid="identita-no-btn" className="bg-red-600 hover:bg-red-700 text-white font-bold">NO</Button>
              <Button size="sm" disabled={saving} onClick={() => setIdentita("da_verificare")} data-testid="identita-da-verificare-btn" variant="outline" className="border-white/20 text-white font-bold hover:bg-white/10">Da verificare</Button>
            </div>
            <p className="text-[11px] text-slate-500">Verifica umana: confronta la foto del tesseramento con il corsista. Nessun riconoscimento biometrico automatico viene eseguito.</p>
          </div>
        </div>
      </div>

      {/* Tabella moduli */}
      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="report-moduli-table">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-400 border-b border-white/10">
              {["Modulo", "Durata", "Video visto", "Webcam attiva", "Presenza", "Data completamento", "Stato", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rep.moduli.map((m) => (
              <tr key={m.id} className="border-b border-white/5">
                <td className="px-4 py-3 font-bold text-white">{m.numero_label} — {m.titolo}</td>
                <td className="px-4 py-3 text-slate-300">{Math.round(m.durata_sec / 60)} min</td>
                <td className="px-4 py-3 text-slate-300">{m.video_pct}%</td>
                <td className="px-4 py-3 text-slate-300">{m.webcam_pct}%</td>
                <td className="px-4 py-3 font-bold text-white">{m.presenza_pct}%</td>
                <td className="px-4 py-3 text-slate-400">{m.completed_at ? fmtDT(m.completed_at) : "—"}</td>
                <td className="px-4 py-3">
                  {m.stato === "completato" ? "✅" : m.stato === "in_corso" ? "🟡" : m.stato === "bloccato" ? "🔒" : "▶️"}
                </td>
                <td className="px-4 py-3">
                  {m.stato !== "completato" && (
                    <button onClick={() => validaModulo(m.id)} data-testid={`valida-modulo-${m.id}`} className="text-[11px] font-bold text-[#f59e0b] hover:underline">Valida manualmente</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sessioni */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
        <h3 className="font-extrabold mb-3">Sessioni recenti ({rep.sessioni.length})</h3>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {rep.sessioni.map((s) => (
            <div key={s.id} className="rounded-lg bg-white/5 px-4 py-2.5 text-[12px] flex flex-wrap gap-x-5 gap-y-1">
              <span className="font-bold text-white">{s.modulo_id}</span>
              <span className="text-slate-400">{fmtDT(s.started_at)} → {s.ended_at ? fmtDT(s.ended_at) : "in corso"}</span>
              <span className="text-slate-300">Video: {s.video_seconds}s</span>
              <span className="text-slate-300">Presenza: {s.presence_seconds}s</span>
              <span className="text-slate-400">Cam off: {s.camera_off_events} · Assenze: {s.absence_events}</span>
            </div>
          ))}
          {rep.sessioni.length === 0 && <div className="text-slate-500 text-[13px]">Nessuna sessione registrata.</div>}
        </div>
      </div>
    </div>
  );
}

function RichiesteVerifica() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/formazione/richieste`, { withCredentials: true })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const decidi = async (rid, esito) => {
    const note = window.prompt("Note (facoltative):") || null;
    await axios.post(`${API}/api/admin/formazione/richieste/${rid}/decidi`, { esito, note }, { withCredentials: true });
    load();
  };
  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  return (
    <div className="space-y-3" data-testid="richieste-verifica-list">
      {rows.map((r) => (
        <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white">{r.nome || r.user_id}</span>
              <span className="text-[11px] text-slate-400">{r.numero_tessera}</span>
              <span className="px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase bg-[#f59e0b]/15 text-[#f59e0b]">{r.tipo}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase ${r.stato === "pending" ? "bg-amber-500/15 text-amber-400" : "bg-white/10 text-slate-300"}`}>{r.stato}</span>
            </div>
            <div className="text-[12.5px] text-slate-300 mt-1">Modulo: {r.modulo_id} · {fmtDT(r.created_at)}</div>
            {r.messaggio && <div className="text-[12.5px] text-slate-400 mt-1 italic">"{r.messaggio}"</div>}
          </div>
          {r.stato === "pending" && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => decidi(r.id, "confermata")} data-testid={`conferma-esito-${r.id}`} variant="outline" className="border-white/20 text-white font-bold hover:bg-white/10">Conferma esito</Button>
              <Button size="sm" onClick={() => decidi(r.id, "modulo_validato")} data-testid={`valida-richiesta-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"><CheckCircle2 className="h-4 w-4 mr-1" /> Valida modulo</Button>
              <Button size="sm" onClick={() => decidi(r.id, "respinta")} className="bg-red-600/80 hover:bg-red-700 text-white font-bold">Respingi</Button>
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && <div className="text-center py-10 text-slate-400">Nessuna richiesta di verifica.</div>}
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/admin/formazione/audit?limit=150`, { withCredentials: true })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-1.5 max-h-[600px] overflow-y-auto" data-testid="audit-log-list">
      {rows.map((a) => (
        <div key={a.id} className="text-[12px] flex flex-wrap gap-x-4 gap-y-0.5 py-1.5 border-b border-white/5">
          <span className="text-slate-500 shrink-0">{fmtDT(a.created_at)}</span>
          <span className="font-bold text-[#f59e0b] uppercase text-[10.5px] tracking-wider">{a.action}</span>
          <span className="text-slate-300">user: {a.user_id?.slice(0, 8)}…</span>
          {a.modulo_id && <span className="text-slate-400">modulo: {a.modulo_id}</span>}
          {a.actor_id && a.actor_id !== a.user_id && <span className="text-amber-400">admin: {a.actor_id.slice(0, 8)}…</span>}
        </div>
      ))}
      {rows.length === 0 && <div className="text-center py-8 text-slate-400">Audit log vuoto.</div>}
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/admin/formazione/settings`, { withCredentials: true }).then((r) => setS(r.data));
  }, []);
  const save = async () => {
    setSaving(true); setOk(false);
    try {
      await axios.put(`${API}/api/admin/formazione/settings`, {
        tolleranza_assenza_sec: Number(s.tolleranza_assenza_sec),
        soglia_presenza_default: Number(s.soglia_presenza_default),
        ripetizione_consentita: !!s.ripetizione_consentita,
      }, { withCredentials: true });
      setOk(true);
    } finally { setSaving(false); }
  };
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 max-w-lg space-y-4" data-testid="formazione-settings">
      <div>
        <label className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Tolleranza assenza temporanea (secondi)</label>
        <Input type="number" min={0} max={300} value={s.tolleranza_assenza_sec} onChange={(e) => setS({ ...s, tolleranza_assenza_sec: e.target.value })} data-testid="settings-tolleranza-input" className="mt-1 bg-white/10 border-white/20 text-white" />
        <p className="text-[11px] text-slate-500 mt-1">Se il volto scompare per meno di questo tempo, la presenza continua a essere conteggiata.</p>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Soglia presenza predefinita (%)</label>
        <Input type="number" min={0} max={100} value={s.soglia_presenza_default} onChange={(e) => setS({ ...s, soglia_presenza_default: e.target.value })} data-testid="settings-soglia-input" className="mt-1 bg-white/10 border-white/20 text-white" />
      </div>
      <label className="flex items-center gap-2 text-[13px] text-slate-300">
        <input type="checkbox" checked={!!s.ripetizione_consentita} onChange={(e) => setS({ ...s, ripetizione_consentita: e.target.checked })} />
        Consenti la ripetizione delle sessioni formative
      </label>
      <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva impostazioni"}
      </Button>
      {ok && <span className="ml-3 text-emerald-400 text-[13px] font-bold">Salvato ✓</span>}
    </div>
  );
}

const EMPTY_MOD = { titolo: "", numero_label: "", gruppo: "", ordine: 99, descrizione: "", obiettivi: [], argomenti: [], video_url: "", durata_sec: 600, pdf_materiale_id: "", soglia_presenza: 80, attivo: true };

function ModuliCrud() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/formazione/moduli`, { withCredentials: true }).then((r) => setRows(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (mid) => {
    if (!window.confirm("Disattivare questo modulo?")) return;
    await axios.delete(`${API}/api/admin/formazione/moduli/${mid}`, { withCredentials: true });
    load();
  };

  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>;
  if (editing) return <ModuloForm modulo={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-3" data-testid="moduli-crud">
      <Button onClick={() => setEditing({ ...EMPTY_MOD })} data-testid="nuovo-modulo-btn" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"><Plus className="h-4 w-4 mr-1.5" /> Nuovo modulo</Button>
      {rows.map((m) => (
        <div key={m.id} className={`rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-4 ${!m.attivo ? "opacity-50" : ""}`}>
          <span className="text-[11px] font-extrabold text-slate-500 w-8">#{m.ordine}</span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white">{m.numero_label} — {m.titolo}</div>
            <div className="text-[11.5px] text-slate-400">{m.gruppo} · {Math.round(m.durata_sec / 60)} min · soglia {m.soglia_presenza}% {m.pdf_materiale_id ? "· 📄 PDF" : ""} {!m.attivo && "· DISATTIVATO"}</div>
          </div>
          <button onClick={() => setEditing(m)} data-testid={`edit-modulo-${m.id}`} className="text-slate-300 hover:text-white"><Pencil className="h-4 w-4" /></button>
          {m.attivo && <button onClick={() => del(m.id)} data-testid={`delete-modulo-${m.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
    </div>
  );
}

function ModuloForm({ modulo, onDone, onCancel }) {
  const [f, setF] = useState({ ...modulo, obiettivi: (modulo.obiettivi || []).join("\n"), argomenti: (modulo.argomenti || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const isNew = !modulo.id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    const payload = {
      titolo: f.titolo, numero_label: f.numero_label, gruppo: f.gruppo, ordine: Number(f.ordine),
      descrizione: f.descrizione, video_url: f.video_url, durata_sec: Number(f.durata_sec),
      pdf_materiale_id: f.pdf_materiale_id || null, soglia_presenza: Number(f.soglia_presenza), attivo: !!f.attivo,
      obiettivi: f.obiettivi.split("\n").map((x) => x.trim()).filter(Boolean),
      argomenti: f.argomenti.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (isNew) await axios.post(`${API}/api/admin/formazione/moduli`, payload, { withCredentials: true });
      else await axios.put(`${API}/api/admin/formazione/moduli/${modulo.id}`, payload, { withCredentials: true });
      onDone();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };
  const cls = "mt-1 bg-white/10 border-white/20 text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4 max-w-2xl" data-testid="modulo-form">
      <h3 className="font-extrabold text-lg">{isNew ? "Nuovo modulo" : `Modifica: ${modulo.titolo}`}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Titolo</label><Input value={f.titolo} onChange={set("titolo")} className={cls} data-testid="modulo-form-titolo" /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Etichetta (es. Modulo 2)</label><Input value={f.numero_label} onChange={set("numero_label")} className={cls} /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Gruppo (es. Modulo 2)</label><Input value={f.gruppo} onChange={set("gruppo")} className={cls} /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Ordine</label><Input type="number" value={f.ordine} onChange={set("ordine")} className={cls} /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Durata video (secondi)</label><Input type="number" value={f.durata_sec} onChange={set("durata_sec")} className={cls} /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Soglia presenza %</label><Input type="number" value={f.soglia_presenza} onChange={set("soglia_presenza")} className={cls} /></div>
      </div>
      <div><label className="text-[11px] uppercase font-bold text-slate-400">URL video</label><Input value={f.video_url} onChange={set("video_url")} className={cls} /></div>
      <div><label className="text-[11px] uppercase font-bold text-slate-400">ID materiale PDF (da sezione Materiali)</label><Input value={f.pdf_materiale_id || ""} onChange={set("pdf_materiale_id")} className={cls} /></div>
      <div><label className="text-[11px] uppercase font-bold text-slate-400">Descrizione</label><Textarea value={f.descrizione} onChange={set("descrizione")} rows={4} className={cls} /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Argomenti (uno per riga)</label><Textarea value={f.argomenti} onChange={set("argomenti")} rows={4} className={cls} /></div>
        <div><label className="text-[11px] uppercase font-bold text-slate-400">Obiettivi (uno per riga)</label><Textarea value={f.obiettivi} onChange={set("obiettivi")} rows={4} className={cls} /></div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving || !f.titolo || !f.video_url} data-testid="modulo-form-save" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modulo"}</Button>
        <Button onClick={onCancel} variant="outline" className="border-white/20 text-white hover:bg-white/10">Annulla</Button>
      </div>
    </div>
  );
}
